import Blockheader from '@pelckmans/elementis/components/blockheader';
import Breadcrumbs from '@pelckmans/elementis/components/breadcrumbs';
import Card from '@pelckmans/elementis/components/card';
import Page from '@pelckmans/elementis/components/page';
import Pill from '@pelckmans/elementis/components/pill';
import MaterialMedia from '@pelckmans/elementis/icons/material-media';
import Results from '@pelckmans/elementis/icons/results';
import Search from '@pelckmans/elementis/icons/search';
import Tasks from '@pelckmans/elementis/icons/tasks';
import groupBy from 'lodash/groupBy';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { ValuesOfObject } from '../../../@types/utils';
import { ExerciseCategory, getExerciseCategories } from '../../../api/exercise-category';
import { getSharedSlideSets, SharedSlideSet } from '../../../api/slide-set';
import { getSharedUserMaterial, SharedUserMaterial } from '../../../api/user-material';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../constants/analyticsEvents';
import IdentityContext from '../../../contexts/IdentityContext';
import MyAssignmentsContext from '../../../contexts/MyAssignmentsContext';
import useAnalytics from '../../../hooks/useAnalytics';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { useRoutes } from '../../../hooks/useRoutes';
import useStudioGroup from '../../../hooks/useStudioGroup';
import ContentBody from '../../../layout/content/ContentBody';
import ContentHeader from '../../../layout/content/ContentHeader';
import {
  withScrollThreshold,
  WithScrollThresholdProps,
} from '../../../shared-components/elementis/hoc/withScrollThreshold';
import LoadingState from '../../../shared-components/LoadingState';
import { buildGroupProps } from '../../../utils/elementisUtils';
import { isStandaloneModule } from '../../../utils/module';
import AppsContext from '../_contexts/AppsContext';
import ModuleContext from '../_contexts/ModuleContext';
import SuperModuleBreadcrumb from '../components/breadcrumbs/SuperModuleBreadcrumb';
import DigibookButton from '../components/DigibookButton';
import { isDigibookApp, isOldDigibookApp } from '../utils/digibookAppUtils';
import ModuleCard from './components/ModuleCard';
import useModuleAccess from '../../../hooks/useModuleAccess';
import { FEATURE_ACCESS } from '../../../core/access/module-access';

interface SubModuleSelectorProps extends WithScrollThresholdProps {}

const getAssignmentsTag = (count: number) =>
  buildGroupProps(
    <Pill role="listitem" prominence="danger" key="assignment-count">
      {count}
    </Pill>,
  );

const getMaterialCountTag = (count: number) =>
  buildGroupProps(
    <Pill role="listitem" prominence="neutral" key="material-count">
      {count}
    </Pill>,
  );

function SubModuleSelector({ pastThreshold }: SubModuleSelectorProps) {
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const studioGroup = useStudioGroup();

  const { module: superModule, subModules } = useContext(ModuleContext);
  const { getIncompleteAssignmentCountByModule } = useContext(MyAssignmentsContext);
  const { apps } = useContext(AppsContext);
  const { identity } = useContext(IdentityContext);
  const { assignmentsAccess, resultsAccess } = useModuleAccess(superModule);

  const { buildRelativePath, buildAbsolutePath } = useRoutes();
  useDocumentTitle(t('PAGES.SUBMODULE_SELECTOR.DOCUMENT_TITLE'));

  const digibooksApps = apps.filter(app => isDigibookApp(app) || isOldDigibookApp(app));
  const { isTeacherAlike } = identity;

  const [sharedUserMaterialByModuleId, setSharedUserMaterialByModuleId] = useState<Record<
    string,
    SharedUserMaterial[]
  > | null>(null);

  const [sharedSlideSetByModuleId, setSharedSlideSetByModuleId] = useState<Record<string, SharedSlideSet[]> | null>(
    null,
  );

  const [exerciseCategories, setExerciseCategories] = useState<ExerciseCategory[]>([]);

  useEffect(() => {
    const fetchExerciseCategories = async () => {
      if (!superModule?.hasExerciseEntrance) {
        setExerciseCategories([]);
        return;
      }

      try {
        const { data } = await getExerciseCategories({
          moduleId: superModule.id,
          includeSubModules: true,
        });

        const general = data.find(({ id }) => id === 'general');
        const rest = data.filter(({ id }) => id !== 'general');

        setExerciseCategories([...(general?.categories?.map(category => ({ ...category, usage: 0 })) || []), ...rest]);
      } catch (error) {
        console.error('Error fetching exercise categories:', error);
        setExerciseCategories([]);
      }
    };

    fetchExerciseCategories();
  }, [superModule]);

  useEffect(() => {
    const fetchSharedUserMaterial = async () => {
      if (identity.isTeacherAlike || !superModule) return;

      try {
        const response = await getSharedUserMaterial(superModule.id, {
          includeSubModules: true,
        });

        const userMaterialByModuleId = groupBy(response.data, 'moduleId');
        setSharedUserMaterialByModuleId(userMaterialByModuleId);
      } catch (error) {
        console.error('Error fetching shared user material:', error);
      }
    };

    fetchSharedUserMaterial();
  }, [superModule, identity.isTeacherAlike]);

  useEffect(() => {
    const fetchSharedSlideSet = async () => {
      if (identity.isTeacherAlike || !superModule) return;

      try {
        const response = await getSharedSlideSets(superModule.id, {
          includeSubModules: true,
        });
        const slideSetByModuleId = groupBy(response.data, 'moduleId');
        setSharedSlideSetByModuleId(slideSetByModuleId);
      } catch (error) {
        console.error('Error fetching shared slide set:', error);
      }
    };

    fetchSharedSlideSet();
  }, [superModule, identity.isTeacherAlike]);

  const captureEvent = (eventType: ValuesOfObject<typeof ANALYTICS_EVENT_TYPES>) => {
    analytics.capture({
      eventType,
      objectId: superModule?.id,
      subLocationId: ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_SUBMODULE_SELECTION,
    });
  };

  const aggregatedModuleCounts = [superModule, ...subModules]
    .filter(module => module)
    .reduce(
      (acc, module) => {
        if (!module) return acc;
        if (module.counts) {
          acc.studentExercises += module.counts.studentExercises;
          acc.teacherExercises += module.counts.teacherExercises;
          acc.studentOtherMaterials += module.counts.studentOtherMaterials;
          acc.teacherOtherMaterials += module.counts.teacherOtherMaterials;
          acc.slideSets += module.counts.slideSets || 0;
        }

        return acc;
      },
      { studentExercises: 0, teacherExercises: 0, studentOtherMaterials: 0, teacherOtherMaterials: 0, slideSets: 0 },
    );

  const aggregatedMaterialCount = useMemo(() => {
    if (identity.isTeacherAlike) {
      return (
        aggregatedModuleCounts.teacherExercises +
        aggregatedModuleCounts.teacherOtherMaterials +
        aggregatedModuleCounts.slideSets
      );
    }

    const totalSharedUserMaterialCount = Object.values(sharedUserMaterialByModuleId || {}).reduce(
      (acc, curr) => acc + curr.length,
      0,
    );
    const totalSharedSlideSetCount = Object.values(sharedSlideSetByModuleId || {}).reduce(
      (acc, curr) => acc + curr.length,
      0,
    );

    return (
      aggregatedModuleCounts.studentExercises +
      aggregatedModuleCounts.studentOtherMaterials +
      totalSharedUserMaterialCount +
      totalSharedSlideSetCount
    );
  }, [aggregatedModuleCounts, sharedSlideSetByModuleId, sharedUserMaterialByModuleId, identity.isTeacherAlike]);

  const hasExercisesToShow = isTeacherAlike
    ? aggregatedModuleCounts.teacherExercises > 0
    : aggregatedModuleCounts.studentExercises > 0;

  const showVirtualExerciseModule =
    Boolean(superModule?.hasExerciseEntrance) && Boolean(exerciseCategories?.length) && hasExercisesToShow;

  if (superModule && isStandaloneModule(superModule)) {
    return (
      <Navigate
        to={buildRelativePath('MATERIAL.CONTENT.TOC', { selectedModuleSlug: superModule.slug, nodeId: '' })}
        replace
      />
    );
  }

  const moduleAssignmentsCount = getIncompleteAssignmentCountByModule(superModule);

  const params = {
    subjectSlug: superModule?.subjectSlug || '',
    methodSlug: superModule?.methodSlug || '',
    moduleSlug: superModule?.slug || '',
  };

  const size = pastThreshold ? 'sm' : 'md';

  return (
    <Page>
      <ContentHeader
        cover={
          <Page.Header
            density={pastThreshold ? 'compact' : 'normal'}
            headerText={t('PAGES.SUBMODULE_SELECTOR.PAGE_TITLE')}
            breadcrumbs={
              <Breadcrumbs aria-label={t('MODULE_BREADCRUMB.A11Y_LABEL')}>
                <SuperModuleBreadcrumb />
              </Breadcrumbs>
            }
            buttonGroupProps={
              studioGroup !== 'BAO'
                ? buildGroupProps(<DigibookButton digibookApps={digibooksApps} size={size} />)
                : undefined
            }
          />
        }
      />
      <ContentBody>
        {superModule ? (
          <>
            <div className="sub-module-selector__card-list">
              <ModuleCard
                module={superModule}
                sharedUserMaterialCount={sharedUserMaterialByModuleId?.[superModule.id]?.length}
                sharedSlideSetCount={sharedSlideSetByModuleId?.[superModule.id]?.length}
              />
              {subModules.map(subModule => (
                <ModuleCard
                  key={subModule.id}
                  module={subModule}
                  sharedUserMaterialCount={sharedUserMaterialByModuleId?.[subModule.id]?.length}
                  sharedSlideSetCount={sharedSlideSetByModuleId?.[subModule.id]?.length}
                />
              ))}
              {showVirtualExerciseModule && (
                <ModuleCard module={superModule} isVirtual exerciseCategories={exerciseCategories} />
              )}
            </div>
            <section className="sub-module-selector__shortcuts" data-testid="shortcuts">
              <Blockheader title={t('PAGES.SUBMODULE_SELECTOR.SHORTCUTS.TITLE')} titleTag="h2" />
              <div className="sub-module-selector__shortcuts__list">
                <Card
                  as="link"
                  titleTag="h4"
                  titleText={t('PAGES.SUBMODULE_SELECTOR.SHORTCUTS.CARDS.CATEGORICAL_MATERIAL')}
                  visualIcon={<MaterialMedia />}
                  href={buildAbsolutePath(['MATERIAL', 'MATERIAL.CATEGORICAL'], params)}
                  tags={aggregatedMaterialCount ? getMaterialCountTag(aggregatedMaterialCount) : undefined}
                  onClick={() => captureEvent(ANALYTICS_EVENT_TYPES.CATEGORICAL_MATERIAL_ACCESSED)}
                />
                {assignmentsAccess !== FEATURE_ACCESS.HIDDEN && (
                  <Card
                    as="link"
                    titleTag="h4"
                    titleText={t('PAGES.SUBMODULE_SELECTOR.SHORTCUTS.CARDS.ASSIGNMENTS')}
                    visualIcon={<Tasks />}
                    href={buildAbsolutePath(['MATERIAL', 'MATERIAL.ASSIGNMENTS'], params)}
                    tags={moduleAssignmentsCount ? getAssignmentsTag(moduleAssignmentsCount) : undefined}
                    onClick={() => captureEvent(ANALYTICS_EVENT_TYPES.ASSIGNMENTS_ACCESSED)}
                  />
                )}

                {resultsAccess !== FEATURE_ACCESS.HIDDEN && (
                  <Card
                    as="link"
                    titleTag="h4"
                    titleText={t('PAGES.SUBMODULE_SELECTOR.SHORTCUTS.CARDS.RESULTS')}
                    visualIcon={<Results />}
                    href={buildAbsolutePath(['MATERIAL', 'MATERIAL.RESULTS'], params)}
                    onClick={() => captureEvent(ANALYTICS_EVENT_TYPES.RESULTS_ACCESSED)}
                  />
                )}
                <Card
                  as="link"
                  titleTag="h4"
                  titleText={t('PAGES.SUBMODULE_SELECTOR.SHORTCUTS.CARDS.SEARCH')}
                  visualIcon={<Search />}
                  href={buildAbsolutePath(['MATERIAL', 'MATERIAL.SEARCH'], params)}
                  onClick={() => captureEvent(ANALYTICS_EVENT_TYPES.SEARCH_ACCESSED)}
                />
              </div>
            </section>
          </>
        ) : (
          <LoadingState variant="list" height={196} />
        )}
      </ContentBody>
    </Page>
  );
}

export default withScrollThreshold(SubModuleSelector);
